import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { SERVICE_TYPE_LABELS, formatINR, formatINRCompact } from "@garage/shared";
import { useSummary, type Summary } from "../api/reports";
import { Alert, Button, Card, Spinner } from "../components/ui";
import { presets } from "../lib/ranges";
import { formatDate } from "../lib/format";

// One hue with varying weight rather than a rainbow: the categories are not
// qualitatively different, they are shares of the same total.
const RAMP = ["#e9a33d", "#cf8f34", "#b57b2b", "#9a6722", "#805419", "#664110", "#4c2f0a"];

export default function DashboardPage() {
  const options = presets();
  const [preset, setPreset] = useState(options[1]!);
  const { data, isPending, error } = useSummary(preset.range);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
        <div className="ml-auto flex flex-wrap gap-1.5">
          {options.map((p) => (
            <button
              key={p.id}
              onClick={() => setPreset(p)}
              aria-pressed={p.id === preset.id}
              className={`rounded border px-2.5 py-1 font-mono text-[11px] transition focus:outline-none focus-visible:ring-2 focus-visible:ring-signal ${
                p.id === preset.id
                  ? "border-signal bg-signal/15 text-signal"
                  : "border-line text-ink-3 hover:text-ink"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {isPending && <div className="mt-8"><Spinner label="Crunching numbers" /></div>}
      {error && <div className="mt-6"><Alert>{(error as Error).message}</Alert></div>}
      {data && <Panels summary={data} />}
    </div>
  );
}

function Panels({ summary: s }: { summary: Summary }) {
  if (s.totals.services === 0) {
    return (
      <Card className="mt-6 p-8 text-center">
        <p className="text-ink-2">
          Nothing logged in this period. Add a service and the numbers appear
          here.
        </p>
        <Link to="/" className="mt-4 inline-block">
          <Button variant="ghost">Go to garage</Button>
        </Link>
      </Card>
    );
  }

  const grand = Number(s.totals.spend) || 1;

  return (
    <div className="mt-6 flex flex-col gap-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile label="Total spend" value={formatINRCompact(s.totals.spend)}
          foot={
            s.previous?.changePct != null
              ? `${s.previous.changePct >= 0 ? "▲" : "▼"} ${Math.abs(s.previous.changePct)}% vs previous`
              : undefined
          }
          tone={s.previous?.changePct != null && s.previous.changePct > 0 ? "warn" : "ok"}
        />
        <Tile label="Services" value={String(s.totals.services)} foot={`${formatINR(s.totals.avgCost)} average`} />
        <Tile
          label="Cost per km"
          value={s.costPerKm ? formatINR(s.costPerKm.value) : "—"}
          foot={s.costPerKm ? `over ${s.costPerKm.kmCovered.toLocaleString("en-IN")} km` : "needs two odometer readings"}
        />
        <Tile
          label="Service interval"
          value={s.cadence.avgDays ? `${s.cadence.avgDays} days` : "—"}
          foot={s.cadence.avgKm ? `about ${s.cadence.avgKm.toLocaleString("en-IN")} km apart` : "needs two periodic services"}
        />
      </div>

      {s.expiring.length > 0 && (
        <Card className="p-4">
          <h2 className="font-mono text-[11px] tracking-[0.12em] text-ink-3 uppercase">
            Expiring soon
          </h2>
          <ul className="mt-3 flex flex-col gap-2">
            {s.expiring.map((e, i) => (
              <li key={i} className="flex flex-wrap items-center gap-2 text-sm">
                <span
                  className={`rounded border px-2 py-0.5 font-mono text-[11px] ${
                    e.daysLeft < 0
                      ? "border-danger/50 bg-danger/10 text-danger"
                      : "border-signal/50 bg-signal/10 text-signal"
                  }`}
                >
                  {e.kind}
                </span>
                <Link to={`/vehicles/${e.vehicleId}`} className="hover:text-signal">
                  {e.label}
                </Link>
                <span className="text-ink-3">
                  {formatDate(e.date)} ·{" "}
                  {e.daysLeft < 0 ? `expired ${Math.abs(e.daysLeft)}d ago` : `${e.daysLeft}d left`}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <h2 className="font-mono text-[11px] tracking-[0.12em] text-ink-3 uppercase">
            Where the money goes
          </h2>
          <ul className="mt-3 flex flex-col gap-2.5">
            {s.byType.map((t, i) => (
              <ShareBar
                key={t.type}
                label={SERVICE_TYPE_LABELS[t.type as keyof typeof SERVICE_TYPE_LABELS] ?? t.type}
                amount={t.total}
                count={t.count}
                share={Number(t.total) / grand}
                color={RAMP[i % RAMP.length]!}
              />
            ))}
          </ul>
        </Card>

        <Card className="p-4">
          <h2 className="font-mono text-[11px] tracking-[0.12em] text-ink-3 uppercase">
            By vehicle
          </h2>
          <ul className="mt-3 flex flex-col gap-2.5">
            {s.byVehicle.map((v, i) => (
              <ShareBar
                key={v.vehicleId}
                label={v.label}
                amount={v.total}
                count={v.count}
                share={Number(v.total) / grand}
                color={RAMP[i % RAMP.length]!}
              />
            ))}
          </ul>
        </Card>
      </div>

      {s.byMonth.length > 1 && (
        <Card className="p-4">
          <h2 className="font-mono text-[11px] tracking-[0.12em] text-ink-3 uppercase">
            Month by month
          </h2>
          <div className="mt-4 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={s.byMonth.map((m) => ({ ...m, value: Number(m.total) }))}>
                <XAxis
                  dataKey="month"
                  tickFormatter={(m: string) => m.slice(5) + "/" + m.slice(2, 4)}
                  tick={{ fill: "#788d95", fontSize: 11 }}
                  axisLine={{ stroke: "#25353b" }}
                  tickLine={false}
                />
                <YAxis
                  tickFormatter={(v) => formatINRCompact(Number(v ?? 0))}
                  tick={{ fill: "#788d95", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={62}
                />
                <Tooltip
                  cursor={{ fill: "#ffffff10" }}
                  contentStyle={{
                    background: "#131e22",
                    border: "1px solid #25353b",
                    borderRadius: 3,
                    fontSize: 12,
                  }}
                  labelStyle={{ color: "#a6b8be" }}
                  formatter={(v) => [formatINR(Number(v ?? 0)), "Spent"]}
                />
                <Bar dataKey="value" radius={[2, 2, 0, 0]}>
                  {s.byMonth.map((_, i) => (
                    <Cell key={i} fill="#e9a33d" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {s.topItems.length > 0 && (
        <Card className="p-4">
          <h2 className="font-mono text-[11px] tracking-[0.12em] text-ink-3 uppercase">
            Most spent on
          </h2>
          <p className="mt-1 text-sm text-ink-3">
            Across the parts and work you itemised.
          </p>
          <ul className="mt-3 grid gap-2 sm:grid-cols-2">
            {s.topItems.map((i) => (
              <li key={i.name} className="flex items-baseline gap-2 text-sm">
                <span className="truncate">{i.name}</span>
                <span className="font-mono text-xs text-ink-3">{i.count}×</span>
                <span className="ml-auto font-mono tabular-nums">{formatINR(i.total)}</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

function Tile({
  label,
  value,
  foot,
  tone,
}: {
  label: string;
  value: string;
  foot?: string;
  tone?: "ok" | "warn";
}) {
  return (
    <Card className="p-4">
      <p className="font-mono text-[10px] tracking-[0.1em] text-ink-3 uppercase">
        {label}
      </p>
      <p className="mt-1.5 text-2xl font-semibold tabular-nums">{value}</p>
      {foot && (
        <p
          className={`mt-1 font-mono text-[11px] ${
            tone === "warn" ? "text-signal" : tone === "ok" ? "text-ok" : "text-ink-3"
          }`}
        >
          {foot}
        </p>
      )}
    </Card>
  );
}

function ShareBar({
  label,
  amount,
  count,
  share,
  color,
}: {
  label: string;
  amount: string;
  count: number;
  share: number;
  color: string;
}) {
  return (
    <li>
      <div className="flex items-baseline gap-2 text-sm">
        <span className="truncate">{label}</span>
        <span className="font-mono text-xs text-ink-3">{count}</span>
        <span className="ml-auto font-mono tabular-nums">{formatINR(amount)}</span>
        <span className="w-10 text-right font-mono text-xs text-ink-3">
          {Math.round(share * 100)}%
        </span>
      </div>
      <div className="mt-1 h-1.5 rounded-sm bg-line">
        <div
          className="h-1.5 rounded-sm"
          style={{ width: `${Math.max(2, share * 100)}%`, background: color }}
        />
      </div>
    </li>
  );
}
